import { authPostConfirmed } from '../../../utils/cloudFunctionsAuth';
import { EmailFolder, isEmailFolder, normalizeEmailFolder } from './emailFolder';
import { OWNER_ONLY_REASON, isForbiddenError, isGraphWriteForbiddenNow, noteGraphWriteForbidden } from './markEmailRead';

/**
 * Client side of the mail "move" actions: archive / back to inbox / junk / delete, undo of
 * each, permanent delete from Deleted Items, and "open in Outlook".
 *
 * The owner's real Outlook is the source of truth, so every move is a server call
 * (moveGraphEmail: a real Graph move + the Firestore doc update, owner-only) - nothing here
 * writes Firestore. The server answers with the location BEFORE and AFTER the move; undo is
 * a move back to `previous`, so the caller only has to keep what moveEmail handed it.
 *
 * Contract for callers: none of these ever throws and none of them toasts. They resolve a
 * result object and the caller decides what to show (the undo toast lives in undoToast.tsx).
 *
 * The owner-only 403 is remembered by markEmailRead (shared with read/flag/junk/delete): once
 * a 403 is known, moves resolve { ok:false, forbidden:true } without sending a doomed request.
 */

const FUNCTIONS_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';
const MOVE_URL = `${FUNCTIONS_BASE}/moveGraphEmail`;
const WEB_LINK_URL = `${FUNCTIONS_BASE}/getGraphEmailWebLink`;
const DELETE_URL = `${FUNCTIONS_BASE}/deleteGraphEmail`;

export type MoveDestination = 'inbox' | 'archive' | 'deleted' | 'junk';

export interface EmailLocation {
  folder: EmailFolder;
  deleted: boolean;
  outlookFolderId?: string;
  outlookFolderName?: string;
  outlookFolderPath?: string;
}

export type MoveResult =
  | { ok: true; previous: EmailLocation; current: EmailLocation; staleId?: boolean }
  | { ok: false; reason: string; forbidden?: boolean };

/**
 * The fields of an email row a move needs. emailType / quarantined are optional extras that
 * let a legacy doc with no `folder` still be recognised as sent / junk (same rule as the
 * server and normalizeEmailFolder); passing a whole email row works.
 */
export interface MoveSubject {
  id: string;
  folder?: string;
  deleted?: boolean;
  outlookFolderId?: string;
  outlookFolderName?: string;
  outlookFolderPath?: string;
  emailType?: string;
  quarantined?: boolean;
}

/** The body moveGraphEmail takes. */
export interface MovePayload {
  emailId: string;
  destination: MoveDestination | 'filed';
  destinationFolderId?: string;
}

const DESTINATIONS: ReadonlyArray<MoveDestination> = ['inbox', 'archive', 'deleted', 'junk'];
/** Folders a message can be moved out of (the server enforces the same list). */
const MOVABLE_FROM: ReadonlySet<EmailFolder> = new Set<EmailFolder>(['inbox', 'junk', 'archive', 'filed', 'deleted']);

const text = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);

function buildLocation(
  folder: EmailFolder,
  detail?: { id?: unknown; name?: unknown; path?: unknown },
): EmailLocation {
  const loc: EmailLocation = { folder, deleted: folder === 'deleted' };
  // Folder detail describes 'filed' mail only (archive / well-known folders carry none).
  if (folder === 'filed' && detail) {
    const id = text(detail.id);
    const name = text(detail.name);
    const path = text(detail.path);
    if (id) loc.outlookFolderId = id;
    if (name) loc.outlookFolderName = name;
    if (path) loc.outlookFolderPath = path;
  }
  return loc;
}

/** Where an email is now. Pure. `deleted` is true exactly when the folder is 'deleted'. */
export function locationOf(e: MoveSubject): EmailLocation {
  return buildLocation(normalizeEmailFolder(e), { id: e.outlookFolderId, name: e.outlookFolderName, path: e.outlookFolderPath });
}

/** The request body for moving to a well-known destination. Pure. */
export function buildMovePayload(emailId: string, destination: MoveDestination): MovePayload {
  return { emailId, destination };
}

/**
 * The move that undoes a move: back to `previous`. Pure.
 * Not restorable: Deleted Items (undoing "out of Deleted" is not offered here), Sent/Drafts
 * (never moved), and filed mail whose Outlook folder id is unknown.
 */
export function restorePayload(emailId: string, previous: EmailLocation): { ok: true; payload: MovePayload } | { ok: false; reason: string } {
  switch (previous.folder) {
    case 'inbox':
    case 'junk':
    case 'archive':
      return { ok: true, payload: buildMovePayload(emailId, previous.folder) };
    case 'filed': {
      const id = text(previous.outlookFolderId);
      if (!id) return { ok: false, reason: "the original folder isn't known" };
      return { ok: true, payload: { emailId, destination: 'filed', destinationFolderId: id } };
    }
    case 'deleted':
      return { ok: false, reason: "it can't be put back into Deleted Items from here" };
    default:
      return { ok: false, reason: 'sent and draft messages cannot be moved' };
  }
}

function locationFrom(raw: unknown, fallback: EmailLocation): EmailLocation {
  if (!raw || typeof raw !== 'object') return fallback;
  const r = raw as Record<string, unknown>;
  if (!isEmailFolder(r.folder)) return fallback;
  return buildLocation(r.folder, { id: r.outlookFolderId, name: r.outlookFolderName, path: r.outlookFolderPath });
}

/**
 * Turn the server's answer into a MoveResult. A field the server left out or got wrong falls
 * back to what the caller expected, so a stale deploy answering just { ok:true } still
 * produces a usable result. staleId is only ever `true` or absent.
 */
export function normalizeMoveResponse(
  raw: unknown,
  fallbackPrevious: EmailLocation,
  fallbackCurrent: EmailLocation,
): Extract<MoveResult, { ok: true }> {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const result: Extract<MoveResult, { ok: true }> = {
    ok: true,
    previous: locationFrom(r.previous, fallbackPrevious),
    current: locationFrom(r.current, fallbackCurrent),
  };
  if (r.staleId === true) result.staleId = true;
  return result;
}

function reasonOf(err: unknown): string {
  const m = (err as any)?.message;
  return typeof m === 'string' && m.trim() ? m.trim() : 'unknown error';
}

const forbiddenResult = (): { ok: false; reason: string; forbidden: true } => ({
  ok: false,
  reason: OWNER_ONLY_REASON,
  forbidden: true,
});

function failureOf(err: unknown): { ok: false; reason: string; forbidden?: boolean } {
  if (isForbiddenError(err)) {
    // markEmailRead owns the "this user is not the owner" memory (shared with read / flag /
    // junk / delete): remember it so the next tap does not send another doomed request.
    noteGraphWriteForbidden();
    return forbiddenResult();
  }
  return { ok: false, reason: reasonOf(err) };
}

async function sendMove(
  payload: MovePayload,
  fallbackPrevious: EmailLocation,
  fallbackCurrent: EmailLocation,
): Promise<MoveResult> {
  // Known owner-only: do not send a request that cannot succeed.
  if (isGraphWriteForbiddenNow()) return forbiddenResult();
  try {
    const raw = await authPostConfirmed(MOVE_URL, payload);
    return normalizeMoveResponse(raw, fallbackPrevious, fallbackCurrent);
  } catch (err) {
    console.warn('[mailActions] move failed:', payload.emailId, payload.destination, reasonOf(err));
    return failureOf(err);
  }
}

/**
 * Move one email to Inbox / Archive / Deleted Items / Junk. The server decides whether it is
 * already there (then previous equals current). Never throws, never toasts.
 */
export async function moveEmail(e: MoveSubject, destination: MoveDestination): Promise<MoveResult> {
  const id = text(e?.id);
  if (!id) return { ok: false, reason: 'missing email id' };
  if (!DESTINATIONS.includes(destination)) return { ok: false, reason: 'unknown destination' };
  const from = locationOf(e);
  if (!MOVABLE_FROM.has(from.folder)) return { ok: false, reason: 'sent and draft messages cannot be moved' };
  return sendMove(buildMovePayload(id, destination), from, buildLocation(destination));
}

/**
 * Undo a move: put the email back where `previous` (from the MoveResult) says it was.
 * Always asks the server - `e` is usually the row as it was BEFORE the move, so its own
 * folder says nothing reliable about where the message is now.
 */
export async function restoreEmail(e: MoveSubject, previous: EmailLocation): Promise<MoveResult> {
  const id = text(e?.id);
  if (!id) return { ok: false, reason: 'missing email id' };
  const plan = restorePayload(id, previous);
  if (!plan.ok) return { ok: false, reason: plan.reason };
  return sendMove(plan.payload, locationOf(e), previous);
}

/**
 * Permanently delete a message that is already in Deleted Items (the existing
 * deleteGraphEmail endpoint removes it from Outlook for good). Anything else is refused here
 * so a stray call can never turn a plain "delete" into a permanent one.
 *
 * The gate is the STORED folder, deliberately stricter than locationOf / normalizeEmailFolder:
 * deleteGraphEmail only hard-deletes when the doc's own folder is 'deleted'; for any other doc
 * (including the pre-fix mislabel deleted:true + folder 'inbox' / none, which the normaliser
 * shows as Deleted Items) it merely MOVES the message to Deleted Items and answers ok:true.
 * Passing the looser gate would report "deleted forever" for mail that is still in Outlook.
 * moveEmail cannot repair such a doc either (the server reads it as already in Deleted Items),
 * so it is refused, never reported as deleted.
 */
export async function deleteEmailForever(e: MoveSubject): Promise<{ ok: true } | { ok: false; reason: string; forbidden?: boolean }> {
  const id = text(e?.id);
  if (!id) return { ok: false, reason: 'missing email id' };
  if (locationOf(e).folder !== 'deleted') return { ok: false, reason: 'only mail in Deleted Items can be deleted forever' };
  if (e.folder !== 'deleted') return { ok: false, reason: "this message isn't confirmed to be in Deleted Items yet, so it was not deleted" };
  if (isGraphWriteForbiddenNow()) return forbiddenResult();
  try {
    await authPostConfirmed(DELETE_URL, { emailId: id });
    return { ok: true };
  } catch (err) {
    console.warn('[mailActions] delete forever failed:', id, reasonOf(err));
    return failureOf(err);
  }
}

/** A link safe to navigate a browser tab to: https, no embedded credentials. Returns the href. */
export function safeHttpsUrl(v: unknown): string | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  try {
    const u = new URL(v.trim());
    if (u.protocol !== 'https:' || !u.hostname || u.username || u.password) return null;
    return u.href;
  } catch {
    return null;
  }
}

// The only hosts "open in Outlook" may navigate to. Mirrors OUTLOOK_WEB_HOSTS in
// functions/src/functions/http/mail-move.ts: the cached webLink lives on emails/{id}, which any
// signed-in app user (or an XSS'd session) can write, so a planted off-host link must never be
// followed just because it is https.
const OUTLOOK_WEB_HOSTS: ReadonlySet<string> = new Set([
  'outlook.office.com',
  'outlook.office365.com',
  'outlook.live.com',
  'outlook.cloud.microsoft',
]);

/** safeHttpsUrl narrowed to an Outlook web host with no explicit port. */
export function safeOutlookUrl(v: unknown): string | null {
  const href = safeHttpsUrl(v);
  if (!href) return null;
  try {
    const u = new URL(href);
    return !u.port && OUTLOOK_WEB_HOSTS.has(u.hostname) ? href : null;
  } catch {
    return null;
  }
}

function closeQuietly(tab: Window): void {
  try { tab.close(); } catch { /* already gone */ }
}

/**
 * Open the message in Outlook on the web, in a new tab.
 *
 * MUST be called synchronously from the click/tap handler: the blank tab is opened BEFORE the
 * first await, because popup blockers (and iOS Safari) only allow window.open inside the user
 * gesture - opening it after the link request resolves gets blocked. The tab is then pointed
 * at the link (the row's cached webLink, else the server's getGraphEmailWebLink) and closed
 * again if that fails. The link is navigated only if it is https.
 */
export async function openInOutlook(e: MoveSubject & { webLink?: string }): Promise<{ ok: true } | { ok: false; reason: string }> {
  const id = text(e?.id);
  if (!id) return { ok: false, reason: 'missing email id' };
  const cached = safeOutlookUrl(e.webLink);
  // Only a server round trip is owner-gated; a link we already hold needs no permission.
  if (!cached && isGraphWriteForbiddenNow()) return { ok: false, reason: OWNER_ONLY_REASON };

  // ---- first statement that touches the DOM, before any await (see above) ----
  const tab = window.open('about:blank', '_blank');
  if (!tab) return { ok: false, reason: 'the browser blocked the new tab - allow pop-ups for this site' };
  // The tab must not be able to reach back into this app through window.opener. (Not passed as
  // a window.open feature: 'noopener' makes open() return null, and the tab is needed below.)
  try { tab.opener = null; } catch { /* best effort */ }
  try { tab.document.body.textContent = 'Opening in Outlook...'; } catch { /* cosmetic only */ }

  let url = cached;
  if (!url) {
    try {
      const res = await authPostConfirmed<{ webLink?: unknown }>(WEB_LINK_URL, { emailId: id });
      url = safeOutlookUrl(res?.webLink);
    } catch (err) {
      closeQuietly(tab);
      console.warn('[mailActions] outlook link failed:', id, reasonOf(err));
      const failed = failureOf(err);
      return { ok: false, reason: failed.reason };
    }
    if (!url) {
      closeQuietly(tab);
      return { ok: false, reason: 'Outlook did not give a usable link' };
    }
  }
  if (tab.closed) return { ok: false, reason: 'the tab was closed before it could open' };
  try {
    tab.location.href = url;
  } catch {
    closeQuietly(tab);
    return { ok: false, reason: 'the browser would not open the link' };
  }
  return { ok: true };
}
